import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import Spinner from '@renderer/components/Spinner'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { FaUndo, FaCreditCard } from 'react-icons/fa'
import { Package } from '@renderer/types/configState'
import { useTranslation } from 'react-i18next'
import { TransactionStatus } from '@renderer/types/sessionState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import PinModal from '@renderer/components/PinModal'
import NewspaperLayout from './components/NewspaperLayout'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

// This is a simplified version that wraps the default Payment logic with newspaper styling
// Most of the payment logic is the same, we just change the UI wrapper
const PaymentPage: React.FC = () => {
  const { t } = useTranslation()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.payment
  const welcomeSettings = getPageSettings('welcome')
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const isMounted = useRef(true)

  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [snapToken, setSnapToken] = useState<string | null>(null)
  const [clientKey, setClientKey] = useState<string | null>(null)
  const [paymentType, setPaymentType] = useState<'core' | 'snap'>('core')
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [showSkipGuide, setShowSkipGuide] = useState(false)
  const [snapPaymentOpened, setSnapPaymentOpened] = useState(false)
  const [showCodeEntry, setShowCodeEntry] = useState(false)
  const [isOnline, setIsOnline] = useState(true)
  const [networkError, setNetworkError] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const countdownPausedAtRef = useRef<number | null>(null)
  const countdownPausedSecondsRef = useRef<number>(0)
  const isOnlineRef = useRef<boolean>(true)
  const { selectedSecondaryCamera, password } = useSettingsStore()
  const {
    id,
    transaction,
    setSessionState,
    userPreferences,
    mode,
    uploadSessionState,
    logActivity
  } = useSessionStateStore()
  const sessionReset = useSessionReset()
  const navigate = useNavigate()
  const { projectId } = useParams()

  const paymentCountdown = useCountdown(0)
  const autoResetCountdown = useCountdown(15)

  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })

    autoResetCountdown.setOnComplete(() => {
      sessionReset()
    })
  }, [])

  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    if (id !== useSessionStateStore.getState().id) {
      logActivity('Ignoring timeout for a previous session.')
      return
    }

    if (!isOnline) {
      safeUpdate(() => {
        paymentCountdown.stop()
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        logActivity('Payment countdown paused - network disconnected', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      })
      return
    }

    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      logActivity('Payment timed out.', {
        orderId: transaction.orderId,
        paymentType
      })
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })
    })
  }

  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      if (isOnline) {
        setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
      }
    })

    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [isOnline])

  const getDefaultPrice = (): number => {
    const packages = globalConfig?.pages.checkout?.settings?.packages || []
    let selectedPackages: Package[] = []

    if (mode === 'capture') {
      selectedPackages = [
        packages.find((pkg) => pkg.type === 'print') || null,
        packages.find((pkg) => pkg.type === 'gif') || null,
        packages.find((pkg) => pkg.type === 'recording') || null
      ].filter((pkg) => pkg !== null)
    } else if (mode === 'record') {
      selectedPackages = [packages.find((pkg) => pkg.type === 'video') || null].filter(
        (pkg) => pkg !== null
      )
    }

    return selectedPackages.reduce((subtotal, pkg) => subtotal + (pkg.price || 0), 0)
  }

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }, [transaction.orderId, transaction.status])

  useEffect(() => {
    const initializePayment = async (): Promise<void> => {
      await Promise.resolve()

      const latestState = useSessionStateStore.getState()
      const latestTransaction = latestState.transaction

      if (latestTransaction.amount !== undefined && latestTransaction.amount !== null) {
        uploadSessionState()
        return
      }

      let calculatedAmount: number

      if (latestTransaction.selectedPackages && latestTransaction.selectedPackages.length > 0) {
        const subtotal = latestTransaction.selectedPackages.reduce(
          (sum, pkg) => sum + Math.round(pkg.price || 0),
          0
        )
        const discount = Math.round(latestTransaction.discount || 0)
        calculatedAmount = Math.max(0, subtotal - discount)
      } else {
        calculatedAmount = getDefaultPrice()
      }

      setSessionState({
        transaction: {
          amount: calculatedAmount
        }
      })

      await Promise.resolve()
      uploadSessionState()
    }

    initializePayment().catch((error) => {
      console.error('Error initializing payment:', error)
      uploadSessionState()
    })

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  useEffect(() => {
    return (): void => {
      if (transaction.orderId && transaction.status !== 'settlement') {
        cleanupPayment()
      }
    }
  }, [transaction.orderId, cleanupPayment])

  const handleSnapPaymentSuccess = async (result: unknown): Promise<void> => {
    console.log('Snap payment successful:', result)
    setIsCheckingPayment(false)
    cogoToast.success(t('payment.successMessage', 'Payment successful!'))
    logActivity('Payment successful via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      snapResult: result
    })
    setSessionState({
      transaction: {
        status: 'settlement'
      }
    })
    uploadSessionState()
    
    // Navigation logic matches view/Payment.tsx exactly
    const filterSettings = getPageSettings('filter')
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    
    let nextPage: string
    if (welcomeSettings?.startWithPayment) {
      if (applyAfterCapture) {
        nextPage = 'frame'
      } else {
        nextPage = 'filter'
      }
    } else {
      nextPage = 'capture'
    }
    
    // Check for secondary camera
    if (
      selectedSecondaryCamera &&
      nextPage === 'filter' &&
      (await unifiedCameraUtils.isDeviceResponding('secondary'))
    ) {
      nextPage = 'camera'
    }
    
    navigate(`/${projectId}/${nextPage}`)
  }

  const handleSnapPaymentError = (result: unknown): void => {
    console.error('Snap payment error:', result)
    if (isOnlineRef.current) {
      cogoToast.error(t('payment.snapError', 'Payment failed. Please try again.'))
    }
    logActivity('Payment failed via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      error: result,
      level: 'error'
    })
    navigate(`/${projectId}`)
  }

  const handleSnapPaymentClose = (): void => {
    setSnapPaymentOpened(false)
    setIsCheckingPayment(true)
  }

  const openSnapPayment = async (): Promise<void> => {
    if (!snapToken || !clientKey || snapPaymentOpened) return

    try {
      window.snap.pay(snapToken, {
        onSuccess: (result: unknown) => {
          handleSnapPaymentSuccess?.(result)
        },
        onError: (result: unknown) => {
          handleSnapPaymentError?.(result)
        },
        onClose: () => {
          handleSnapPaymentClose?.()
        }
      })
    } catch (error) {
      console.error('Error opening Snap payment:', error)
      throw error
    }
  }

  const initiatePayment = async (): Promise<void> => {
    // Already settled (e.g. cash voucher): skip payment and navigate to next page
    if (transaction.status === 'settlement') {
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      const nextPage = welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : 'capture'
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    if (!isOnline) {
      setNetworkError(true)
      logActivity('Payment initiation blocked - no internet connection', {
        isOnline: false
      })
      return
    }

    setNetworkError(false)

    try {
      const data = await MidtransUtils.initiatePayment(
        transaction.amount || getDefaultPrice(),
        id || '',
        config?.settings.paymentTimeout || 120,
        projectId || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        transaction.discount || 0
      )

      if (data.order_id) {
        const safePaymentType = data.payment_type === 'snap' ? 'snap' : 'core'
        setPaymentType(safePaymentType)

        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        const sessionData = {
          transaction: {
            orderId: data.order_id,
            qrUrl: '',
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus,
            amount: transaction.amount,
            discount: transaction.discount || 0,
            paymentType: data.payment_type
          }
        }

        logActivity('Payment initiated.', {
          amount: transaction.amount,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds,
          paymentType: data.payment_type
        })

        if (data.payment_type === 'core' && data.qr_url) {
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          sessionData.transaction.qrUrl = sanitizedQrUrl
        } else if (data.payment_type === 'snap' && data.snap_token) {
          setSnapToken(data.snap_token)
          setClientKey(data.client_key || '')
        }

        setSessionState(sessionData)
        setIsExpired(false)

        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()

        countdownPausedAtRef.current = null
        countdownPausedSecondsRef.current = 0

        setIsCheckingPayment(true)
        isMounted.current = true
      }
    } catch (e: unknown) {
      const error = e as Error
      const isNetworkError =
        !isOnline || error.message.includes('fetch') || error.message.includes('network')

      if (isNetworkError) {
        setNetworkError(true)
        logActivity('Payment initiation failed - network error', {
          error: error.message,
          isOnline,
          level: 'error'
        })
      } else {
        logActivity(`Payment initiation failed: ${error.message}`, {
          error: error.message,
          level: 'error'
        })
      }
      console.error('Error initiating payment:', e)
      if (isOnlineRef.current) {
        cogoToast.error(<span>{error.message}</span>)
      }
    }
  }

  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  useEffect(() => {
    if (isOnline) {
      initiatePayment()
    }

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [isOnline])

  useEffect(() => {
    if (!isOnline) {
      if (paymentCountdown.getRemainingSeconds() > 0) {
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        paymentCountdown.stop()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        setIsCheckingPayment(false)
        logActivity('Network disconnected during payment', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      }
    } else {
      if (countdownPausedAtRef.current !== null && countdownPausedSecondsRef.current > 0) {
        const pausedAt = countdownPausedAtRef.current
        const pausedSeconds = countdownPausedSecondsRef.current
        const elapsedSeconds = Math.floor((Date.now() - pausedAt) / 1000)
        const adjustedRemainingSeconds = Math.max(0, pausedSeconds - elapsedSeconds)

        if (adjustedRemainingSeconds > 0 && transaction.orderId) {
          paymentCountdown.setRemainingSeconds(adjustedRemainingSeconds)
          paymentCountdown.start()
          setIsCheckingPayment(true)
          setNetworkError(false)
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          logActivity('Network reconnected during payment', {
            orderId: transaction.orderId,
            adjustedRemainingSeconds,
            elapsedSeconds
          })
        } else {
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          checkPaymentStatus()
        }
      } else if (networkError && transaction.orderId) {
        setIsCheckingPayment(true)
        setNetworkError(false)
        logActivity('Network reconnected during payment - resuming status checks', {
          orderId: transaction.orderId
        })
      }
    }
  }, [isOnline])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment && isOnline ? 5000 : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    if (!transaction.expiryTime || !transaction.serverTime || !isMounted.current) {
      return
    }

    if (!isOnline) {
      if (isButtonClick) {
        setNetworkError(true)
        cogoToast.error(t('payment.networkError', 'Network error occurred'))
      }
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          logActivity('Payment expired.', {
            orderId: transaction.orderId,
            expiryTime: transaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })
          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(async () => {
          setIsCheckingPayment(false)
          cogoToast.success(t('payment.successMessage', 'Payment successful!'))
          logActivity('Payment successful.', {
            orderId: transaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'settlement'
            }
          })
          uploadSessionState()
          
          // Navigation logic matches view/Payment.tsx exactly
          const filterSettings = getPageSettings('filter')
          const applyAfterCapture = filterSettings?.applyAfterCapture || false
          
          let nextPage: string
          if (welcomeSettings?.startWithPayment) {
            if (applyAfterCapture) {
              nextPage = 'frame'
            } else {
              nextPage = 'filter'
            }
          } else {
            nextPage = 'capture'
          }
          
          // Check for secondary camera
          if (
            selectedSecondaryCamera &&
            nextPage === 'filter' &&
            (await unifiedCameraUtils.isDeviceResponding('secondary'))
          ) {
            nextPage = 'camera'
          }
          
          navigate(`/${projectId}/${nextPage}`)
        })
      } else if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error(t('payment.notSuccessful', 'Payment not successful. Please try again.'))
          }
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error((e as Error).message)
          }
        })
      }
    }
  }

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenSkipPaymentGuide')
    if (!hasSeenGuide) {
      setShowSkipGuide(true)
      localStorage.setItem('hasSeenSkipPaymentGuide', 'true')

      const timer = setTimeout(() => {
        setShowSkipGuide(false)
      }, 10000)

      return (): void => clearTimeout(timer)
    }
    return
  }, [])

  const handleCodeEntry = (pin: string): void => {
    if (pin === password) {
      const customPath = welcomeSettings?.customPath
      const nextPage =
        customPath === 'booth-daughter'
          ? 'capture'
          : welcomeSettings?.startWithPayment
            ? 'filter'
            : 'capture'
      useSessionStateStore
        .getState()
        .logActivity('Payment bypassed with code in offline mode', { to: nextPage })
      navigate(`/${projectId}/${nextPage}`)
      setShowCodeEntry(false)
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  const handleRetry = async (): Promise<void> => {
    if (!isOnline) {
      cogoToast.error(t('payment.offline', 'No internet connection'))
      return
    }

    setIsRetrying(true)
    setNetworkError(false)

    logActivity('Payment retry initiated', {
      isOnline,
      orderId: transaction.orderId
    })

    try {
      await initiatePayment()
    } catch (error) {
      console.error('Error retrying payment:', error)
    } finally {
      setIsRetrying(false)
    }
  }

  const renderPaymentInterface = (): JSX.Element => {

    if (networkError && !isOnline && transaction.orderId) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full">
          <div className="text-center">
            <FaCreditCard className="text-newspaper-black mx-auto mb-3 w-16 h-16" />
            <p className="text-newspaper-black font-bold mb-2 text-lg">
              {t('payment.offline', 'No internet connection')}
            </p>
            <p className="text-newspaper-black font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a] mb-4">
              {t('payment.offlineMessage', 'Please check your internet connection and try again')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-3 px-8 text-lg"
              >
                {t('payment.reload', 'Reload')}
              </button>
              <button
                onClick={() => setShowCodeEntry(true)}
                className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-3 px-8 text-base"
              >
                {t('payment.enterCode', 'Enter Code to Continue')}
              </button>
            </div>
          </div>
        </div>
      )
    }

    if (!isOnline && !transaction.orderId) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full">
          <FaCreditCard className="text-newspaper-black mx-auto mb-3 w-16 h-16" />
          <div className="text-center">
            <p className="text-newspaper-black font-bold mb-3 text-lg">
              {t('payment.offlineMode', 'Offline Mode - No Internet')}
            </p>
            <button
              onClick={() => setShowCodeEntry(true)}
              className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 hover:opacity-90 mb-3 bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-3 px-8 text-lg"
            >
              {t('payment.enterCode', 'Enter Code to Continue')}
            </button>
          </div>
          <p className="text-newspaper-black text-center font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a]">
            {t(
              'payment.offlineInstruction',
              'Payment processing is unavailable offline. Enter your operator code to continue.'
            )}
          </p>
        </div>
      )
    }

    if (isExpired) {
      return (
        <div
          className="flex flex-col items-center justify-center w-full h-full cursor-pointer transition-all duration-300 hover:opacity-90"
          role="button"
          data-nav-to="welcome"
          onClick={(): void => {
            autoResetCountdown.stop()
            sessionReset()
          }}
        >
          <FaUndo className="text-newspaper-black mx-auto mb-3 w-16 h-16" />
          <div className="text-center">
            <p className="text-newspaper-black font-bold mb-2 text-lg">
              {t('payment.expired', 'Payment code expired')}
            </p>
            <p className="text-newspaper-black font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a] mb-2">
              {t('payment.restartPrompt', 'Click to restart transaction')}
            </p>
            {remainingTime && (
              <p className="text-newspaper-black font-bold mb-3 text-base">
                {t('payment.autoRestart', 'Auto-restart in')} {remainingTime}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              autoResetCountdown.stop()
              sessionReset()
            }}
            className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 hover:opacity-90 bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-3 px-8 text-base"
          >
            {t('payment.restart', 'Restart Transaction')}
          </button>
        </div>
      )
    }

    if (paymentType === 'core' && qrUrl) {
      return (
        <img
          src={qrUrl}
          alt={t('payment.qrCodeAlt', 'QR Code')}
          className="w-full h-full object-contain"
          crossOrigin="anonymous"
        />
      )
    }

    if (paymentType === 'snap' && snapToken) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full">
          <FaCreditCard className="text-newspaper-black mx-auto mb-3 w-16 h-16" />
          <div className="text-center">
            <p className="text-newspaper-black font-bold mb-3 text-lg">
              {t('payment.snapReady', 'Payment gateway ready')}
            </p>
            <button
              onClick={openSnapPayment}
              disabled={snapPaymentOpened}
              className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 mb-3 bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-3 px-8 text-lg"
            >
              {snapPaymentOpened
                ? t('payment.snapOpening', 'Opening payment...')
                : t('payment.openSnap', 'Open Payment')}
            </button>
          </div>
          <p className="text-newspaper-black text-center font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a]">
            {t(
              'payment.snapInstruction',
              'Click the button above to open the payment window and complete your transaction.'
            )}
          </p>
        </div>
      )
    }

    return <Spinner />
  }

  return (
    <NewspaperLayout disableOverflow={true}>
      <div className="flex flex-col h-full relative overflow-hidden">
        {showSkipGuide && (
          <div className="absolute top-16 right-12 z-50 bg-newspaper-white p-3 border-2 border-double border-[#2a2a2a] max-w-xs">
            <div className="relative">
              <p className="text-newspaper-black font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a]">
                {t(
                  'payment.skipGuide',
                  'Need to skip payment? Click the money icon in the top right corner and enter the PIN.'
                )}
              </p>
              <button
                type="button"
                onClick={(): void => setShowSkipGuide(false)}
                className="absolute -top-2 -right-2 text-newspaper-black hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Top Section: Vertically Centered Layout */}
        <div className="w-full max-w-4xl mx-auto flex-shrink-0 border-2 border-double border-[#2a2a2a] bg-newspaper-white p-4 mb-2 flex flex-col items-center justify-center min-h-[60vh] py-24">
          {/* Title at Top */}
          <h1 className="font-newspaper font-bold text-5xl leading-tight uppercase tracking-wider text-[#1a1a1a] text-center mb-6">
            {t('payment.payNowTitle', 'PAY TO START SESSION')}
          </h1>

          {/* QRIS Logo in Center */}
          <div className="flex justify-center mb-6">
            <img
              src={'/assets/images/qris.png'}
              alt={t('payment.qrisLogoAlt', 'QRIS logo')}
              className="h-16"
            />
          </div>

          {/* QR Code Container - Fixed size to prevent shrinking */}
          <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white flex justify-center items-center mb-6 p-3" style={{ width: '448px', height: '448px', minWidth: '448px', minHeight: '448px' }}>
            {renderPaymentInterface()}
          </div>

          {/* Timeout and Price Below */}
          <div className="flex flex-col items-center gap-8">
            {remainingTime && (
              <p className="text-newspaper-black/80 font-bold text-5xl mt-10">
                {remainingTime}
              </p>
            )}
            <p className="text-newspaper-black font-bold text-7xl">
              {formatAmount(transaction.amount || getDefaultPrice(), projectCurrency)}
            </p>
          </div>
        </div>


        <PinModal
          isOpen={showCodeEntry}
          onClose={() => setShowCodeEntry(false)}
          onSubmit={handleCodeEntry}
          title={t('payment.codeEntry.title', 'Enter Access Code')}
          subtitle={t(
            'payment.codeEntry.subtitle',
            'Enter 6-digit code to bypass payment in offline mode'
          )}
        />
      </div>
    </NewspaperLayout>
  )
}

export default PaymentPage
